// Radha — dunning ladder (Zoho Invoice/Billing style escalation). Reads the
// tenant's steps; the panel falls back to DEFAULT_LADDER when a tenant has none.
import { useQuery } from "@tanstack/react-query";

import { supabase } from "../lib/supabase";

export interface DunningStep {
  seq: number; min_dpd: number; label: string; channel: string; tone: string;
}

export const DEFAULT_LADDER: DunningStep[] = [
  { seq: 1, min_dpd: 1, label: "Gentle reminder", channel: "email", tone: "friendly" },
  { seq: 2, min_dpd: 15, label: "Firm reminder", channel: "email", tone: "firm" },
  { seq: 3, min_dpd: 30, label: "Final notice", channel: "whatsapp", tone: "final" },
  { seq: 4, min_dpd: 60, label: "Pre-legal escalation", channel: "email", tone: "escalation" },
];

export function useDunningSteps() {
  return useQuery({
    queryKey: ["dunning-steps"],
    queryFn: async (): Promise<DunningStep[]> => {
      const { data, error } = await supabase
        .from("dunning_steps")
        .select("seq, min_dpd, label, channel, tone")
        .order("seq", { ascending: true });
      if (error) throw error;
      return (data ?? []) as DunningStep[];
    },
    staleTime: 60_000,
  });
}

/** The furthest ladder step an account at `dpd` days past due has reached. */
export function stepForDpd(steps: DunningStep[], dpd: number): DunningStep | null {
  let hit: DunningStep | null = null;
  for (const s of steps) if (dpd >= s.min_dpd && (!hit || s.min_dpd > hit.min_dpd)) hit = s;
  return hit;
}

import { fmtMoneyFull } from "../lib/money";
const rupees = (n: number) => fmtMoneyFull(n);

/** A respectful, RBI-safe message body for a tone. No threats / prohibited words. */
export function dunningBody(tone: string, debtor: string, outstanding: number, dpd: number): string {
  const amt = rupees(outstanding);
  switch (tone) {
    case "firm":
      return `Dear ${debtor}, ${amt} has been outstanding for ${dpd} days. Please arrange payment promptly to avoid further follow-up.`;
    case "final":
      return `Final notice: ${amt} remains unpaid after ${dpd} days. Kindly clear the dues at the earliest to avoid escalation.`;
    case "escalation":
      return `Dear ${debtor}, ${amt} is overdue by ${dpd} days. This account is being prepared for escalation; please contact us to resolve it.`;
    default:
      return `Dear ${debtor}, a gentle reminder that ${amt} is outstanding (${dpd} days). We would appreciate settlement at your earliest convenience.`;
  }
}
